import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Container, 
  Typography,
  Grid,
  Divider
} from '@mui/material';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { CONST_BUSINESS_NAME } from './common/Helper';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { FaYoutube, FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';

export default function AppointmentCreatedSuccess() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Appointment Created Successfully - Clouds Analytics";
    
    // Add meta robots tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    let hadMetaRobots = !!metaRobots;
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    const originalContent = metaRobots.content;
    metaRobots.content = 'follow, noindex';

    return () => {
      if (hadMetaRobots) {
        if (originalContent) {
          metaRobots.content = originalContent;
        } else {
          metaRobots.removeAttribute('content');
        }
      } else {
        if (metaRobots && metaRobots.parentNode) {
          metaRobots.parentNode.removeChild(metaRobots);
        }
      }
    };
  }, []);

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: '#F8F9FA'
      }}
    >
      {/* Dark Header */}
      <Box 
        sx={{ 
          height: '70px', 
          backgroundColor: '#061D36', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          px: { xs: 2, md: 3 },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box 
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => window.location.href = 'https://cloudsanalytics.ai/'}
        >
          <img 
            src="/CloudsAnalytics-logo.png" 
            alt={CONST_BUSINESS_NAME} 
            style={{ height: '36px' }}
          />
        </Box>
        <Button
          onClick={() => navigate(CONST_ROUTE_PATH.SIGN_UP)}
          sx={{
            backgroundColor: '#FF9A00',
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '8px',
            padding: '8px 20px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#e68a00',
              boxShadow: 'none'
            }
          }}
        >
          Sign Up
        </Button>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, px: 2 }}>
        <Container maxWidth="sm">
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: '16px', 
              border: '1px solid #E4E7EC', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              backgroundColor: '#ffffff',
              padding: { xs: '32px 24px', sm: '48px 40px' }
            }}
          >
            <CardContent sx={{ p: 0, textAlign: 'center' }}>
              {/* Checkmark Icon Container */}
              <Box 
                sx={{ 
                  width: '64px', 
                  height: '64px', 
                  borderRadius: '50%', 
                  backgroundColor: '#E8F8F5', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 24px'
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2ECC71" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </Box>

              {/* Title */}
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#101828',
                  fontSize: { xs: '24px', sm: '28px' },
                  mb: 1.5
                }}
              >
                Appointment created successfully
              </Typography>

              {/* Subtitle */}
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#475467', 
                  lineHeight: 1.5,
                  fontSize: '16px',
                  mb: 4
                }}
              >
                Thank you for scheduling an appointment with us.
                <br />
                Please check your email inbox for further details.
              </Typography>

              {/* Promotion Message */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#475467', 
                  fontSize: '14px',
                  mb: 3
                }}
              >
                Haven't signed up yet? We invite you to explore our services by signing up now.
              </Typography>

              {/* Separator line */}
              <Divider sx={{ my: 3, borderColor: '#EAECF0' }} />

              {/* Buttons Row */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  gap: 2, 
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Button 
                  variant="contained" 
                  onClick={() => navigate(CONST_ROUTE_PATH.SIGN_UP)}
                  sx={{ 
                    backgroundColor: '#175CD3', 
                    color: '#ffffff', 
                    textTransform: 'none', 
                    fontWeight: 600,
                    fontSize: '15px',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    boxShadow: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#124CA3',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Sign up now
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.href = 'https://www.cloudsanalytics.ai/pricing.html'}
                  sx={{ 
                    borderColor: '#D0D5DD', 
                    color: '#344054', 
                    backgroundColor: '#ffffff',
                    textTransform: 'none', 
                    fontWeight: 600,
                    fontSize: '15px',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    width: { xs: '100%', sm: 'auto' },
                    borderWidth: '1px',
                    '&:hover': {
                      borderColor: '#D0D5DD',
                      backgroundColor: '#F9FAFB',
                      borderWidth: '1px'
                    }
                  }}
                >
                  View pricing
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.href = 'http://cloudsanalytics.ai/'}
                  sx={{ 
                    borderColor: '#D0D5DD', 
                    color: '#344054', 
                    backgroundColor: '#ffffff',
                    textTransform: 'none', 
                    fontWeight: 600,
                    fontSize: '15px',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    width: { xs: '100%', sm: 'auto' },
                    borderWidth: '1px',
                    '&:hover': {
                      borderColor: '#D0D5DD',
                      backgroundColor: '#F9FAFB',
                      borderWidth: '1px'
                    }
                  }}
                >
                  Back to home
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Dark Footer */}
      <Box 
        sx={{ 
          backgroundColor: '#061D36', 
          color: '#ffffff',
          pt: 6,
          pb: 4,
          px: { xs: 4, md: 8 }
        }}
      >
        <Grid container spacing={4} sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          {/* Logo & Social Links */}
          <Grid item xs={12} md={4} sx={{ textAlign: 'left' }}>
            <img 
              src="/CloudsAnalytics-logo.png" 
              alt={CONST_BUSINESS_NAME} 
              style={{ height: '36px', marginBottom: '20px' }} 
            />
            <Box sx={{ display: 'flex', gap: '12px', mt: 1 }}>
              <a href="https://facebook.com" target="_blank" rel="noreferrer" style={{ 
                width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'all 0.2s' 
              }}>
                <FaFacebookF size={14} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" style={{ 
                width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'all 0.2s' 
              }}>
                <FaInstagram size={16} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" style={{ 
                width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'all 0.2s' 
              }}>
                <FaLinkedinIn size={14} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" style={{ 
                width: '38px', height: '38px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'all 0.2s' 
              }}>
                <FaYoutube size={16} />
              </a>
            </Box>
          </Grid>

          {/* Resources Menu */}
          <Grid item xs={12} sm={6} md={4} sx={{ textAlign: 'left' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: '700', mb: 2, color: '#ffffff' }}>
              Resources
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link to={CONST_ROUTE_PATH.TERMS_CONDITIONS} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: '14px' }}>Terms and Conditions</Link>
              <Link to={CONST_ROUTE_PATH.PRIVACY_POLICY} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link>
              <Link to={CONST_ROUTE_PATH.REFUND_POLICY} style={{ color: '#cbd5e1', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link>
            </Box>
          </Grid>

          {/* Contact Details */}
          <Grid item xs={12} sm={6} md={4} sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: { xs: 0, md: 5 } }}>
              <Typography variant='body2' sx={{ color: '#cbd5e1', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                <PhoneIcon sx={{ marginRight: '10px', color: '#ffa000', fontSize: '20px' }} />
                (+91) 9773600508
              </Typography>
              <Typography variant='body2' sx={{ color: '#cbd5e1', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                <MailOutlineIcon sx={{ marginRight: '10px', color: '#ffa000', fontSize: '20px' }} />
                support@cloudsanalytics.ai
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright */}
        <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 6, pt: 3, textAlign: 'center' }}>
          <Typography variant='caption' sx={{ color: '#94a3b8' }}>
            ©2026 Clouds Analytics Inc. All Rights Reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
